import React, { useRef, useState } from 'react';
import { useOutsideClick } from '@hooks/useOutsideClick';

export { toBackendDate, toInputDate } from './dateUtils';

// ── Types ─────────────────────────────────────────────────────────────────────

export type LogsDateRangePreset = '1h' | '24h' | '7d' | '30d' | 'custom';

export interface LogsDateRangeValue {
  preset: LogsDateRangePreset;
  /** `MM-DD-YYYY` — set only when `preset === 'custom'` (matches the backend's format). */
  from?: string;
  /** `MM-DD-YYYY` — set only when `preset === 'custom'`. */
  to?: string;
}

interface LogsDateRangeFilterProps {
  value: LogsDateRangeValue;
  /** Applies a preset immediately — presets need no confirmation step. */
  onChange: (value: LogsDateRangeValue) => void;
  /**
   * Fires when "Custom range" is picked. Deliberately separate from
   * `onChange`: everything else in the bar now auto-applies as soon as it
   * changes, but a bare `preset: 'custom'` with no From/To yet isn't a
   * fetchable range, so it must not go through `onChange` and trigger a
   * request. This only tells `LogsFilterBar` to reveal its custom-range row;
   * that row's own Apply button is what eventually calls `onChange`.
   */
  onSelectCustom: () => void;
  disabled?: boolean;
}

/** Default date range shown on initial load — "Last 7 days" (was briefly "Last 30 days" per an earlier revision of sc-44216). */
export const LOGS_DEFAULT_DATE_RANGE: LogsDateRangeValue = { preset: '7d' };

const PRESET_LABELS: Record<Exclude<LogsDateRangePreset, 'custom'>, string> = {
  '1h': 'Last 1 hour',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};

const PRESET_OPTIONS = (Object.keys(PRESET_LABELS) as Exclude<LogsDateRangePreset, 'custom'>[]).map(
  (preset) => ({ preset, label: PRESET_LABELS[preset] })
);

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Date-range dropdown trigger for the Logs page (sc-44216) — a purpose-built
 * sibling to `components/ui/DateRangeFilter`, whose month-based presets
 * (current/last month, etc.) don't match this story's trailing-window
 * presets (1h/24h/7d/30d) or its MM-DD-YYYY custom-range contract.
 *
 * This component only renders the trigger + preset list. Per Figma (node
 * 2771-9908), the actual From/To inputs for "Custom range" live in a
 * dedicated row of `LogsFilterBar` — between the search row and the
 * status/company/event-type row — not inside this dropdown. Every other
 * filter in the bar (including the presets here) auto-applies the moment it
 * changes; "Custom range" is the one exception, since picking it alone
 * isn't a complete, fetchable range yet — see `onSelectCustom`.
 */
const LogsDateRangeFilter: React.FC<LogsDateRangeFilterProps> = ({
  value,
  onChange,
  onSelectCustom,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useOutsideClick(containerRef, () => setIsOpen(false), isOpen);

  // sc-45715 AC2: once a custom range is applied, the trigger should read
  // "Custom" — short and stable — instead of the literal
  // MM-DD-YYYY From/To values, which crowded the bar and broke the
  // "<Label>: <value>" convention every other filter follows.
  const triggerLabel = value.preset === 'custom' ? 'Custom' : PRESET_LABELS[value.preset];

  function handlePresetClick(preset: Exclude<LogsDateRangePreset, 'custom'>) {
    onChange({ preset });
    setIsOpen(false);
  }

  function handleCustomRangeClick() {
    // Unlike handlePresetClick, this does NOT call onChange — an empty
    // custom range has nothing to fetch yet. It only tells the parent to
    // reveal its From/To row; that row's own Apply is what actually commits.
    onSelectCustom();
    setIsOpen(false);
  }

  return (
    <div ref={containerRef} className="lf-date-range-root">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => !disabled && setIsOpen((o) => !o)}
        className={`lf-date-range-trigger${isOpen ? ' lf-date-range-trigger--open' : ''}`}
      >
        <span className="lf-date-range-value">{triggerLabel}</span>
        <svg
          className={`lf-chevron${isOpen ? ' lf-chevron--up' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="lf-date-range-dropdown" role="listbox">
          {PRESET_OPTIONS.map(({ preset, label }) => (
            <button
              key={preset}
              type="button"
              role="option"
              aria-selected={value.preset === preset}
              className={`lf-date-range-option${
                value.preset === preset ? ' lf-date-range-option--active' : ''
              }`}
              onClick={() => handlePresetClick(preset)}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            role="option"
            aria-selected={value.preset === 'custom'}
            className={`lf-date-range-option${
              value.preset === 'custom' ? ' lf-date-range-option--active' : ''
            }`}
            onClick={handleCustomRangeClick}
          >
            Custom range
          </button>
        </div>
      )}
    </div>
  );
};

export default LogsDateRangeFilter;
